import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Paper,
  Grid,
  Button,
  Card,
  Typography,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Event,
  VideoCall,
  People,
  LocationOn,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import Layout from "../components/layout/Cslayout";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, isSameDay } from "date-fns";
import config from "../config.js";

const CsCalenderPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [newEvent, setNewEvent] = useState({
    title: "",
    date: new Date(),
    time: new Date(),
    type: "Meeting",
    location: "",
    attendees: "",
    isVirtual: false,
  });

  const { API_BASE_URL } = config;
  const today = new Date();

  // Get user from Redux store
  const { user } = useSelector((state) => state.auth);
  const userId = user?.id || user?._id;

  const fetchEvents = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; 
      
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/events/${year}/${month}`,
        {
          headers: {
            'Authorization': `Bearer ${userId}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      
  
      
      if (Array.isArray(response.data)) {
        setEvents(response.data);
        setError(null);
      } else {
        console.error('Invalid response format:', response.data);
        setError('Unexpected response format from server');
      }
      
    } catch (error) {
      console.error('Fetch error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
  
      setError(error.response?.data?.error || 'Error fetching events');
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error fetching events',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (error) {
      console.error('Calendar error state:', error);
    }
  }, [error]);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      setError("User authentication required");
      return;
    }
    
    setLoading(true);
    setError(null);
  
    try {
      const eventData = {
        title: newEvent.title,
        date: newEvent.date,
        time: format(newEvent.time, "HH:mm"),
        type: newEvent.type,
        location: newEvent.location,
        isVirtual: newEvent.isVirtual,
        attendees: newEvent.attendees ? newEvent.attendees.split(",").map(a => a.trim()) : []
      };
  
    
  
      if (selectedEvent) {
        const response = await axios.put(
          `${API_BASE_URL}/api/v1/events/${selectedEvent._id}`,
          eventData,
          {
            headers: {
              'Authorization': `Bearer ${userId}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );
       
        setSnackbar({
          open: true,
          message: "Event updated successfully",
          severity: "success"
        });
      } else {
        // Create new event
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/events`, 
          eventData,
          {
            headers: {
              'Authorization': `Bearer ${userId}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );
        
        setSnackbar({
          open: true,
          message: "Event created successfully",
          severity: "success"
        });
      }
  
      setOpenDialog(false);
      setSelectedEvent(null);
      setNewEvent({
        title: "",
        date: new Date(),
        time: new Date(),
        type: "Meeting",
        location: "",
        attendees: "",
        isVirtual: false
      });
      await fetchEvents();
    } catch (error) {
      console.error('Submit error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: error.config?.data
      });
      
      const errorMessage = error.response?.data?.error || "Failed to save event";
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!userId) {
      setError("User authentication required");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/events/${id}`, {
        headers: {
          'Authorization': `Bearer ${userId}`
        }
      });
      setSnackbar({
        open: true,
        message: "Event deleted successfully",
        severity: "success",
      });
      setOpenDialog(false);
      await fetchEvents();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Error deleting event";
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCalendarDays = () => {
    const daysInMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    ).getDay();

    const days = [];
    const previousMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - 1,
      1
    );
    const daysInPreviousMonth = new Date(
      previousMonth.getFullYear(),
      previousMonth.getMonth() + 1,
      0
    ).getDate();

    // Previous month days
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(
        previousMonth.getFullYear(),
        previousMonth.getMonth(),
        daysInPreviousMonth - i
      );
      days.push({ day: daysInPreviousMonth - i, isCurrentMonth: false, date });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        i
      );
      days.push({ day: i, isCurrentMonth: true, date });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        i
      );
      days.push({ day: i, isCurrentMonth: false, date });
    }

    return days;
  };

  const getEventsForDate = (date) => {
    return events.filter((event) => isSameDay(new Date(event.date), date));
  };

  const EventCard = ({ event }) => (
    <motion.div whileHover={{ scale: 1.02 }}>
      <Card
        sx={{
          p: 2,
          mb: 1,
          cursor: "pointer",
          "&:hover": { boxShadow: 6 },
        }}
        onClick={() => {
          setSelectedEvent(event);
          setNewEvent({
            ...event,
            date: new Date(event.date),
            time: new Date(`2000-01-01T${event.time}`),
            attendees: event.attendees.join(", "),
          });
          setOpenDialog(true);
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {event.title}
          </Typography>
          <Chip
            size="small"
            label={event.type}
            color={event.type === "Meeting" ? "primary" : "warning"}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Event fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {event.time}
          </Typography>
          {event.isVirtual && <VideoCall fontSize="small" color="primary" />}
        </Box>
        {event.location && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {event.location}
            </Typography>
          </Box>
        )}
        {event.attendees?.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <People fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {event.attendees.join(", ")}
            </Typography>
          </Box>
        )}
      </Card>
    </motion.div>
  );

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Calendar header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={handlePreviousMonth}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h4">
                {format(currentDate, "MMMM yyyy")}
              </Typography>
              <IconButton onClick={handleNextMonth}>
                <ChevronRight />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedEvent(null);
                setNewEvent({
                  ...newEvent,
                  date: new Date(),
                  time: new Date(),
                });
                setOpenDialog(true);
              }}
            >
              Add Event
            </Button>
          </Box>

          {/* Calendar grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2 }}>
                <Grid container sx={{ mb: 2 }}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <Grid key={day} item xs={12 / 7}>
                        <Typography
                          align="center"
                          color="text.secondary"
                          sx={{ fontWeight: "medium" }}
                        >
                          {day}
                        </Typography>
                      </Grid>
                    )
                  )}
                </Grid>
                <Divider sx={{ mb: 2 }} />
                <Grid container>
                  {generateCalendarDays().map((day, index) => {
                    const dayEvents = getEventsForDate(day.date);
                    const isToday = isSameDay(day.date, today);

                    return (
                      <Grid
                        key={index}
                        item
                        xs={12 / 7}
                        sx={{ aspectRatio: "1/1", p: 0.5 }}
                      >
                        <motion.div whileHover={{ scale: 1.05 }}>
                          <Paper
                            elevation={day.isCurrentMonth ? 1 : 0}
                            sx={{
                              height: "100%",
                              p: 1,
                              bgcolor: day.isCurrentMonth
                                ? "background.paper"
                                : "background.default",
                              border: isToday ? 2 : 0,
                              borderColor: isToday
                                ? "error.main"
                                : "transparent",
                              cursor: "pointer",
                              position: "relative",
                              overflow: "hidden",
                            }}
                            onClick={() => {
                              setSelectedEvent(null);
                              setNewEvent({
                                ...newEvent,
                                date: day.date,
                              });
                              setOpenDialog(true);
                            }}
                          >
                            <Typography
                              align="center"
                              color={
                                !day.isCurrentMonth
                                  ? "text.disabled"
                                  : isToday
                                  ? "error.main"
                                  : "text.primary"
                              }
                              sx={{
                                fontWeight: isToday ? "bold" : "regular",
                                mb: 1,
                              }}
                            >
                              {day.day}
                            </Typography>
                            {dayEvents.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                  justifyContent: "center",
                                }}
                              >
                                {dayEvents.map((event, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor:
                                        event.type === "Meeting"
                                          ? "primary.main"
                                          : "warning.main",
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Paper>
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            {/* Upcoming events sidebar */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Upcoming Events
                </Typography>
                {events
                  .filter((event) => new Date(event.date) >= today)
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((event) => (
                    <EventCard key={event._id} event={event} />
                  ))}
              </Paper>
            </Grid>
          </Grid>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Event dialog */}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <form onSubmit={handleSubmit}>
              <DialogTitle>
                {selectedEvent ? "Edit Event" : "Add New Event"}
              </DialogTitle>
              <DialogContent>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <TextField
                    label="Title"
                    value={newEvent.title}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, title: e.target.value })
                    }
                    required
                    fullWidth
                  />
                  <div className="custom-datepicker-wrapper">
                    <DatePicker
                      selected={newEvent.date}
                      onChange={(date) => setNewEvent({ ...newEvent, date })}
                      dateFormat="MMMM d, yyyy"
                      className="custom-datepicker"
                      customInput={
                        <TextField fullWidth label="Date" required />
                      }
                    />
                  </div>
                  <div className="custom-datepicker-wrapper">
                    <DatePicker
                      selected={newEvent.time}
                      onChange={(time) => setNewEvent({ ...newEvent, time })}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="custom-datepicker"
                      customInput={
                        <TextField fullWidth label="Time" required />
                      }
                    />
                  </div>
                  <TextField
                    label="Type"
                    value={newEvent.type}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, type: e.target.value })
                    }
                    select
                    required
                    fullWidth
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="Meeting">Meeting</option>
                    <option value="Deadline">Deadline</option>
                    <option value="Task">Task</option>
                    <option value="Other">Other</option>
                  </TextField>
                  <TextField
                    label="Location"
                    value={newEvent.location}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, location: e.target.value })
                    }
                    required
                    fullWidth
                  />
                  <TextField
                    label="Attendees (comma-separated)"
                    value={newEvent.attendees}
                    onChange={(e) =>
                      setNewEvent({ ...newEvent, attendees: e.target.value })
                    }
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newEvent.isVirtual}
                        onChange={(e) =>
                          setNewEvent({
                            ...newEvent,
                            isVirtual: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Virtual Meeting"
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                {selectedEvent && (
                  <Button
                    onClick={() => handleDeleteEvent(selectedEvent._id)}
                    color="error"
                    disabled={loading}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setOpenDialog(false);
                    setSelectedEvent(null);
                    setNewEvent({
                      title: "",
                      date: new Date(),
                      time: new Date(),
                      type: "Meeting",
                      location: "",
                      attendees: "",
                      isVirtual: false,
                    });
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={loading}>
                  {selectedEvent ? "Update" : "Add"}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        </motion.div>
      </Box>
    </Layout>
  );
};


const styles = `
.custom-datepicker-wrapper {
  width: 100%;
}

.custom-datepicker {
  width: 100%;
}

.react-datepicker-wrapper {
  width: 100%;
}

.react-datepicker__input-container {
  width: 100%;
}

.react-datepicker__input-container input {
  width: 100%;
  height: 56px;
  padding: 16.5px 14px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  background-color: transparent;
}

.react-datepicker__input-container input:focus {
  border-color: #1976d2;
  border-width: 2px;
  outline: none;
}

.react-datepicker__input-container input:hover {
  border-color: rgba(0, 0, 0, 0.87);
}
`;


const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default CsCalenderPage;
